import queryString from 'query-string';

import { AttributeI } from '~src/interfaces/attributes.types';
import { CompanyRespWithSettingAttributes } from '~src/interfaces/company.interface';
import { localeToLanguage, SessionUserI, SystemLocales } from '~src/interfaces/session.types';
import { StoreI } from '~src/rootReducer';
import { getLanguageFromRFC5646LanguageTag, LanguageCode, parsePosixLocale } from '~src/services/locale';

import { getContentLanguageAttribute, getSystemLanguageAttribute } from './attributes';

export function getAvailableSystemLanguages(state: StoreI) {
  return getAvailableSystemLocales(state).map(localeToLanguage);
}

export function getAvailableSystemLocales(state: StoreI) {
  return Object.keys(state.user.session.systemLocales);
}

export function getSystemLanguage(state: StoreI) {
  return localeToLanguage(getSystemLocale(state));
}


export function getCompanyContentLocale(state: StoreI) {
  return (
    localeFromUser(getContentLanguageAttribute(state), state.user.session.company) ||
    state.user.session.defaultContentLocale
  );
}

export function getContentLocale(state: StoreI, withDefault = true) {
  return (
    localeFromUser(getContentLanguageAttribute(state), state.user.session.user) ||
    (withDefault ? state.user.session.defaultContentLocale : '')
  );
}

export function getBrowserLanguage() {
  return parsePosixLocale(getBrowserPreferredSystemLocale()).language;
}

export function getCompanySystemLocale(state: StoreI) {
  return (
    localeFromUser(getSystemLanguageAttribute(state), state.user.session.company) ||
    state.user.session.defaultSystemLocale
  );
}

export function getSystemLocale(state: StoreI): SystemLocales {
  return (
    localeFromUser(getSystemLanguageAttribute(state), state.user.session.user) ||
    state.user.session.defaultSystemLocale
  ) as SystemLocales;
}

export function getDefaultSystemLanguage(state: StoreI) {
  return localeToLanguage(getDefaultSystemLocale(state));
}

export function getDefaultSystemLocale(state: StoreI) {
  return state.user.session.defaultSystemLocale;
}

export function isSystemLocaleAllowed(state: StoreI, locale: string) {
  const systemLocaleKeys = Object.keys(state.user.session.systemLocales);
  for (const i in systemLocaleKeys) {
    if (systemLocaleKeys[i] === locale) {
      return true;
    }
  }

  return false;
}

export function localeFromUser(
  attribute: AttributeI,
  user: SessionUserI | CompanyRespWithSettingAttributes | null,
): string {
  if (!attribute || !user) {
    return '';
  }
  if (
    user.setting_attributes &&
    Object.keys(user.setting_attributes).length > 0 &&
    user.setting_attributes[attribute.uuid]
  ) {
    return String(user.setting_attributes[attribute.uuid]);
  }

  return '';
}

export function getBrowserTimezone(): string {
  return Intl ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'not detected';
}

const availableSystemLocales: Record<LanguageCode, string> = {
  en: 'en_US',
  de: 'de_DE',
}

function getDefaultSystemLocaleFromLanguages(languageCodes: LanguageCode[]): string {
  const matchingLocale = languageCodes.find((languageCode) => Boolean(availableSystemLocales[languageCode]));

  return matchingLocale ? availableSystemLocales[matchingLocale] : availableSystemLocales.en;
}

// Gets users browser locale, fallback will be default if nothing found
export function getBrowserPreferredSystemLocale() {
  const languageTags =
    navigator.languages || [navigator.language]; // Fallback for *very* old browsers, see https://caniuse.com/?search=navigator.languages
  const languageCodes: LanguageCode[] = languageTags.map(getLanguageFromRFC5646LanguageTag);

  return getDefaultSystemLocaleFromLanguages(languageCodes);
}

export function redeemTokenReturnedTokenErrorRedirect(location: Location) {
  const searchParams = queryString.parse(location.search);
  delete searchParams.auth_token;
  const searchParamsWithoutAuthToken = queryString.stringify(searchParams);

  return `${location.pathname}?${searchParamsWithoutAuthToken}`;
}

export function authTokenUsedButNoManageMeSetRedirect(location: Location) {
  const searchParams = queryString.parse(location.search);
  delete searchParams.auth_token;
  const searchParamsWithoutAuthToken = queryString.stringify(searchParams);
  const redirect = `${location.pathname}?${searchParamsWithoutAuthToken}`;

  return `/password${location.search}&location=${redirect}&secureAccount=1`;
}
